// ============================================================
//  src/perfil-datos.js — Operaciones Firestore del perfil de usuario
// ============================================================

import {
  collection, query, where, getDocs,
  doc, getDoc, setDoc, writeBatch,
  serverTimestamp, Timestamp, limit
} from 'firebase/firestore';

import { db } from './firebase.js';
import { logInfo } from './core/logger.js';
import { manejarError } from './core/errors.js';

const MAX_OPS_BATCH = 400;

// ============================================================
//  Perfil de usuario
// ============================================================

export async function cargarPerfil(idUsuario) {
  try {
    const refDoc = doc(db, 'users', idUsuario);
    const snapshot = await getDoc(refDoc);

    if (!snapshot.exists()) return null;

    const datos = snapshot.data();

    return {
      ...datos,
      fechaRegistro: datos.fechaRegistro?.toDate?.() ?? null,
      ultimoAcceso: datos.ultimoAcceso?.toDate?.() ?? null,
    };
  } catch (error) {
    manejarError(error, 'Firestore cargarPerfil', { mostrar: false });
    return null;
  }
}

export async function guardarPerfil(idUsuario, datos) {
  try {
    const refDoc = doc(db, 'users', idUsuario);
    const datosFirestore = { ...datos };

    if (datos.fechaRegistro instanceof Date) {
      datosFirestore.fechaRegistro = Timestamp.fromDate(datos.fechaRegistro);
    }

    if (datos.ultimoAcceso instanceof Date) {
      datosFirestore.ultimoAcceso = Timestamp.fromDate(datos.ultimoAcceso);
    }

    await setDoc(refDoc, datosFirestore, { merge: true });

    logInfo('Firestore', `Perfil guardado ${idUsuario}`);
    return true;
  } catch (error) {
    manejarError(error, 'Firestore guardarPerfil');
    return false;
  }
}

export async function guardarIdiomaEnPerfil(userId, idioma) {
  try {
    const refDoc = doc(db, 'users', userId);

    await setDoc(refDoc, { preferencias: { idioma } }, { merge: true });

    logInfo('Firestore', `Idioma guardado ${idioma}`);
  } catch (error) {
    manejarError(error, 'Firestore guardarIdioma', { mostrar: false });
  }
}

export async function guardarOnboardingCompletado(userId) {
  try {
    const refDoc = doc(db, 'users', userId);

    await setDoc(refDoc, {
      preferencias: {
        onboardingCompletado: true,
        onboardingCompletadoEn: serverTimestamp()
      }
    }, { merge: true });

    logInfo('Firestore', `Onboarding completado ${userId}`);
  } catch (error) {
    manejarError(error, 'Firestore guardarOnboardingCompletado', { mostrar: false });
  }
}

// ============================================================
//  Archivos de usuario
// ============================================================

/**
 * Retorna true si el usuario tiene al menos un archivo en alguna de sus ediciones.
 * @param {string[]} edicionIds — IDs de las ediciones del usuario (de memoria, sin query extra)
 */
export async function usuarioTieneArchivos(edicionIds) {
  for (const id of edicionIds) {
    try {
      const snap = await getDocs(query(collection(db, 'editions', id, 'files'), limit(1)));
      if (!snap.empty) return true;
    } catch { /* edición sin archivos o sin permisos — continuar */ }
  }
  return false;
}

// ============================================================
//  Eliminación completa de cuenta
// ============================================================

export async function eliminarCuentaCompleta(idUsuario, onProgreso = () => {}) {
  try {

    onProgreso(5, 'Buscando tus datos en Firestore...');

    const consultaEdiciones = query(
      collection(db, 'editions'),
      where('userId', '==', idUsuario)
    );

    const consultaSolicitudes = query(
      collection(db, 'solicitudesConcurso'),
      where('solicitadoPor', '==', idUsuario)
    );

    const [snapshotEdiciones, snapshotSolicitudes] = await Promise.all([
      getDocs(consultaEdiciones),
      getDocs(consultaSolicitudes)
    ]);

    const ediciones = snapshotEdiciones.docs;
    const solicitudes = snapshotSolicitudes.docs;

    const refsAEliminar = [];
    const driveFileIds = new Set();
    const driveFolderIds = new Set();

    let totalArchivos = 0;

    onProgreso(12, `Analizando ${ediciones.length} concurso/s...`);

    for (let i = 0; i < ediciones.length; i++) {

      const edicionDoc = ediciones[i];
      const datosEdicion = edicionDoc.data();

      _agregarFolderIdsOrdenados(driveFolderIds, datosEdicion.folderIds);

      const colFiles = collection(db, 'editions', edicionDoc.id, 'files');
      const snapFiles = await getDocs(colFiles);

      snapFiles.docs.forEach((fileDoc) => {
        refsAEliminar.push(fileDoc.ref);

        const datos       = fileDoc.data() ?? {};
        const storageType = datos.storageType ?? 'user_drive';
        const fileId      = datos.fileId || datos.driveId || null;

        if (storageType === 'user_drive' && fileId) {
          driveFileIds.add(fileId);
        }
      });

      totalArchivos += snapFiles.docs.length;
      refsAEliminar.push(edicionDoc.ref);

      const progreso = 12 + Math.round(((i + 1) / Math.max(ediciones.length, 1)) * 38);
      onProgreso(progreso, `Preparando borrado del concurso ${i + 1} de ${ediciones.length}...`);
    }

    solicitudes.forEach((docSnap) => refsAEliminar.push(docSnap.ref));

    refsAEliminar.push(doc(db, 'users', idUsuario));
    refsAEliminar.push(doc(db, 'shacks', idUsuario));

    const totalRefs = refsAEliminar.length;

    onProgreso(55, `Eliminando ${totalRefs} documento/s de Firestore...`);

    let eliminados = 0;

    while (refsAEliminar.length > 0) {
      const lote = refsAEliminar.splice(0, MAX_OPS_BATCH);
      const batch = writeBatch(db);

      lote.forEach((refDoc) => batch.delete(refDoc));
      await batch.commit();

      eliminados += lote.length;

      const progreso = 55 + Math.round((eliminados / Math.max(totalRefs, 1)) * 17);
      onProgreso(progreso, `Eliminados ${eliminados} de ${totalRefs} documento/s...`);
    }

    const resultado = {
      firestore: {
        ediciones: ediciones.length,
        archivos: totalArchivos,
        solicitudes: solicitudes.length,
        documentosEliminados: eliminados,
        perfil: true,
        shack: true
      },
      drive: {
        archivoIds: Array.from(driveFileIds),
        folderIds: Array.from(driveFolderIds)
      }
    };

    logInfo('Firestore', 'Cuenta eliminada en Firestore', resultado);

    return resultado;

  } catch (error) {
    manejarError(error, 'Firestore eliminarCuentaCompleta');
    throw error;
  }
}

// ============================================================
//  Helper interno
// ============================================================

function _agregarFolderIdsOrdenados(setDestino, folderIds) {
  if (!folderIds || typeof folderIds !== 'object') return;

  const orden = [
    'log',
    'conf',
    'res',
    'cert',
    'reg',
    'modo',
    'año',
    'concurso',
    'indicativo',
    'raiz'
  ];

  orden.forEach((clave) => {
    const valor = folderIds[clave];
    if (typeof valor === 'string' && valor.trim()) {
      setDestino.add(valor.trim());
    }
  });
}
